import type { ReactNode } from 'react';
import { Navigate } from 'react-router';

import { useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ROUTES } from '@/shared/config/routes';

interface ManagedToolGuardProps {
  /** The managed-tool id to evaluate access for (e.g. 'test-content'). */
  toolId: string;
  children: ReactNode;
}

/**
 * Route guard that enforces per-workspace ACL for a synthetic "managed tool".
 * Wrap INSIDE the relevant `FeatureFlagGuard` so the flag remains the master
 * switch and this guard refines visibility per workspace. Redirects to home
 * when the tool is not allowed in the current workspace.
 */
export const ManagedToolGuard = ({ toolId, children }: ManagedToolGuardProps) => {
  const workspaceId = useCurrentWorkspaceId();
  const organizationId = getCurrentOrganizationId();

  const { isLoading, isAgentEnabled } = useAgentVisibility([toolId], {
    workspaceId,
    organizationId,
  });

  // Wait for a workspace + the access check before deciding, to avoid a
  // premature redirect during early mount (the query is disabled until a
  // workspace id is available).
  if (!workspaceId || isLoading) {
    return null;
  }

  if (!isAgentEnabled(toolId)) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};
